/**
 * One booking bar on the calendar lane + its popover with customer / pickup /
 * drop-off / total / agreement link details.
 *
 * Pulled out of BookingsCalendar so the main component can focus on layout +
 * gesture state.
 */
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileText } from "lucide-react";
import Money from "@/components/Money";
import { formatRangeDay, formatTime, STATUS_STYLE, bookingBarStatus } from "@/components/calendar/calendarHelpers";
import { Phone, Undo2, CalendarPlus } from "lucide-react";

export default function CalendarBookingBar({ booking, left, width, car, scope, highlighted = false, onEarlyReturn, onExtend }) {
  const statusKey = bookingBarStatus(booking);
  const style = STATUS_STYLE[statusKey] || STATUS_STYLE.confirmed;
  const bookingRef = `#${String(booking.id).slice(0, 8).toUpperCase()}`;
  const paymentLabel = booking.status === "pending_payment" ? "Pending payment" : "Paid";
  const isActive = statusKey === "active";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`absolute top-7 h-7 rounded-md border ${style.bg} ${style.text} ${style.border} text-[11px] font-medium px-1.5 truncate hover:brightness-110 transition cursor-pointer text-left shadow-sm ${highlighted ? "ring-2 ring-offset-1 ring-blue-500 z-10 animate-pulse" : ""}`}
          style={{ left, width: Math.max(20, width) }}
          data-testid={`cal-booking-${booking.id}`}
          data-bar-status={statusKey}
        >
          {/* Iter 100 — green dot marks an early return (vehicle freed). */}
          {booking.early_return && <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 ring-1 ring-white mr-1 align-middle" data-testid={`cal-early-dot-${booking.id}`} />}
          {booking.customer_name}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm" align="start">
        <div className="space-y-2" data-testid={`cal-booking-popover-${booking.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-mono text-[11px] font-bold text-blue-700" data-testid={`cal-booking-ref-${booking.id}`}>{bookingRef}</div>
              <div className="font-semibold">{booking.customer_name}</div>
              {booking.customer_email && (
                <div className="text-xs text-neutral-500 truncate">{booking.customer_email}</div>
              )}
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
              {style.label}
            </span>
          </div>
          <div className="border-t border-border pt-2 space-y-1.5 text-xs">
            {car && (car.make || car.model) && (
              <div>
                <div className="text-neutral-400 uppercase tracking-wider text-[10px] font-semibold">Vehicle</div>
                <div className="font-medium">{car.make} {car.model}</div>
                {scope === "admin" && car.vendor_name && (
                  <div className="text-neutral-500">{car.vendor_name}</div>
                )}
              </div>
            )}
            <div>
              <div className="text-neutral-400 uppercase tracking-wider text-[10px] font-semibold">Pick-up</div>
              <div className="font-medium">
                {formatRangeDay(booking.start)}{formatTime(booking.start) && ` · ${formatTime(booking.start)}`}
              </div>
              {booking.pickup_location && <div className="text-neutral-500">{booking.pickup_location}</div>}
            </div>
            <div>
              <div className="text-neutral-400 uppercase tracking-wider text-[10px] font-semibold">Drop-off</div>
              <div className="font-medium">
                {formatRangeDay(booking.end)}{formatTime(booking.end) && ` · ${formatTime(booking.end)}`}
              </div>
              {booking.dropoff_location && <div className="text-neutral-500">{booking.dropoff_location}</div>}
            </div>
            {booking.amount > 0 && (
              <div className="pt-1 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500">Total</span>
                  <span className="font-semibold"><Money amount={booking.amount} /></span>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-neutral-500">Payment</span>
              <span className={`font-medium ${booking.status === "pending_payment" ? "text-amber-700" : "text-emerald-700"}`} data-testid={`cal-booking-payment-${booking.id}`}>
                {paymentLabel}
              </span>
            </div>
            {booking.deposit_status && (
              <div className="text-neutral-500 capitalize">
                Deposit: {booking.deposit_status.replace(/_/g, " ")}
              </div>
            )}
            {booking.early_return && (
              <div className="text-emerald-700 font-medium" data-testid={`cal-early-note-${booking.id}`}>
                ✓ Returned early{booking.scheduled_end ? ` (scheduled ${formatRangeDay(booking.scheduled_end)})` : ""}
              </div>
            )}
            <Link
              to={`/agreements/${booking.id}`}
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2 font-medium"
              data-testid={`cal-booking-agreement-${booking.id}`}
            >
              <FileText className="h-3 w-3" /> View rental agreement
            </Link>
            {/* Iter 100 — quick actions straight from the calendar. */}
            <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-border mt-2">
              {booking.customer_phone && (
                <a href={`tel:${booking.customer_phone}`} className="inline-flex items-center justify-center gap-1 rounded-lg border border-border py-1.5 text-xs font-bold" data-testid={`cal-contact-${booking.id}`}>
                  <Phone className="h-3 w-3" /> Contact
                </a>
              )}
              {isActive && onEarlyReturn && (
                <button type="button" onClick={() => onEarlyReturn(booking, car)} className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-600 text-white py-1.5 text-xs font-bold" data-testid={`cal-early-return-${booking.id}`}>
                  <Undo2 className="h-3 w-3" /> Check In
                </button>
              )}
              {(statusKey === "active" || statusKey === "confirmed") && onExtend && (
                <button type="button" onClick={() => onExtend(booking, car)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-border py-1.5 text-xs font-bold" data-testid={`cal-extend-${booking.id}`}>
                  <CalendarPlus className="h-3 w-3" /> Extend
                </button>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
